<template>
    <div>
        <div ref="editor-container">
            <div v-if="editor" class="mb-1 w-auto">
                <div class="d-flex align-items-center">
                    <div class="btn-group flex-grow-0 flex-shrink-0" @click="showUrlTools = false">
                        <div class="btn-group" role="group">
                            <button
                                type="button"
                                class="btn btn-sm btn-light"
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                            >
                                <div class="text-label">
                                    <div class="font-weight-bold align-text-top toolbar-toggle">
                                        <span
                                            class="align-middle"
                                            v-show="editor.isActive('paragraph', { class: 'custom-p-normal' })"
                                            >Normal text</span
                                        >
                                        <span
                                            class="align-middle"
                                            v-show="editor.isActive('paragraph', { class: 'custom-p-sub' })"
                                            >Secondary</span
                                        >
                                        <span class="align-middle" v-show="editor.isActive('heading', { level: 1 })"
                                            >Heading 1&nbsp;</span
                                        >
                                        <span class="align-middle" v-show="editor.isActive('heading', { level: 2 })"
                                            >Heading 2&nbsp;</span
                                        >
                                        <span class="align-middle" v-show="editor.isActive('heading', { level: 3 })"
                                            >Heading 3&nbsp;</span
                                        >
                                        <span class="align-middle" v-show="mixedTextActive">Mix</span>
                                        <span
                                            class="align-middle icon icon-glyphicons-basic-chevron-down icon-chevron"
                                        ></span>
                                    </div>
                                </div>
                            </button>
                            <div class="dropdown-menu shadow-sm table-dropdown">
                                <a
                                    class="dropdown-item font-weight-bold small px-3"
                                    @click="editor.chain().focus().setParagraph().run()"
                                    :class="{ active: editor.isActive('paragraph', { class: 'custom-p-normal' }) }"
                                >
                                    Normal text
                                </a>
                                <a
                                    class="dropdown-item font-weight-bold small px-3"
                                    @click="editor.chain().focus().setParagraph({ class: 'custom-p-sub' }).run()"
                                    :class="{ active: editor.isActive('paragraph', { class: 'custom-p-sub' }) }"
                                >
                                    Secondary text
                                </a>
                                <a
                                    class="dropdown-item font-weight-bold small px-3"
                                    @click="editor.chain().focus().toggleHeading({ level: 1 }).run()"
                                    :class="{ active: editor.isActive('heading', { level: 1 }) }"
                                >
                                    Heading 1
                                </a>
                                <a
                                    class="dropdown-item font-weight-bold small px-3"
                                    @click="editor.chain().focus().toggleHeading({ level: 2 }).run()"
                                    :class="{ active: editor.isActive('heading', { level: 2 }) }"
                                >
                                    Heading 2
                                </a>
                                <a
                                    class="dropdown-item font-weight-bold small px-3"
                                    @click="editor.chain().focus().toggleHeading({ level: 3 }).run()"
                                    :class="{ active: editor.isActive('heading', { level: 3 }) }"
                                >
                                    Heading 3
                                </a>
                            </div>
                        </div>
                        <button
                            class="btn btn-sm btn-light"
                            @click="editor.chain().focus().toggleBold().run()"
                            :class="{ active: editor.isActive('bold') }"
                        >
                            <span
                                class="icon-lg icon icon-glyphicons-basic-bold align-middle toolbar-vert-adjust"
                            ></span>
                        </button>
                        <button
                            class="btn btn-sm btn-light"
                            @click="editor.chain().focus().toggleItalic().run()"
                            :class="{ active: editor.isActive('italic') }"
                        >
                            <span
                                class="icon-lg icon icon-glyphicons-basic-italic align-middle toolbar-vert-adjust"
                            ></span>
                        </button>
                        <button
                            class="btn btn-sm btn-light"
                            @click="editor.chain().focus().toggleUnderline().run()"
                            :class="{ active: editor.isActive('underline') }"
                        >
                            <span class="icon-lg icon icon-glyphicons-basic-underline align-middle"></span>
                        </button>
                        <div class="btn-group" role="group">
                            <button
                                type="button"
                                class="btn btn-sm btn-light toolbar-toggle"
                                :class="{ active: editor.isActive({ fontSize: /.*?px|pt|rem|em/ }) }"
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                            >
                                <span
                                    class="icon icon-glyphicons-basic-text align-middle"
                                    style="font-size: 125%"
                                ></span>
                                <span
                                    class="align-middle icon icon-glyphicons-basic-chevron-down icon-chevron ml-0"
                                ></span>
                            </button>
                            <div class="dropdown-menu shadow-sm toolbar-dropdown">
                                <a
                                    v-for="fontSize in fontSizes"
                                    :key="fontSize"
                                    class="dropdown-item dropdown-font-size"
                                    :class="{ 'text-primary': editor.isActive({ fontSize: fontSize }) }"
                                    @click="editor.chain().focus().setFontSize(fontSize).run()"
                                    >{{ fontSize }}</a
                                >
                                <div class="dropdown-divider"></div>
                                <a
                                    class="dropdown-item dropdown-font-size"
                                    @click="editor.chain().focus().unsetFontSize().run()"
                                    >Reset</a
                                >
                            </div>
                        </div>
                        <button
                            class="btn btn-sm btn-light"
                            @click="editor.chain().focus().unsetSuperscript().toggleSubscript().run()"
                            :class="{ active: editor.isActive('subscript') }"
                        >
                            <span class="icon-lg icon icon-glyphicons-basic-subscript align-text-top"></span>
                        </button>
                        <button
                            class="btn btn-sm btn-light"
                            @click="editor.chain().focus().unsetSubscript().toggleSuperscript().run()"
                            :class="{ active: editor.isActive('superscript') }"
                        >
                            <span class="icon-lg icon icon-glyphicons-basic-superscript align-text-top"></span>
                        </button>
                        <div class="btn-group" role="group">
                            <button
                                type="button"
                                class="btn btn-sm btn-light toolbar-toggle"
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                            >
                                <span
                                    class="icon-lg icon align-text-top"
                                    :class="{
                                        'icon-glyphicons-basic-paragraph-center': editor.isActive({
                                            textAlign: 'center',
                                        }),
                                        'icon-glyphicons-basic-paragraph-right': editor.isActive({
                                            textAlign: 'right',
                                        }),
                                        'icon-glyphicons-basic-paragraph-left':
                                            textAlignIsInactive || editor.isActive({ textAlign: 'left' }),
                                    }"
                                >
                                </span>
                                <span class="align-middle icon icon-glyphicons-basic-chevron-down icon-chevron"></span>
                            </button>
                            <div class="dropdown-menu shadow-sm toolbar-dropdown">
                                <a
                                    v-for="alignment in ['left', 'center', 'right']"
                                    :key="alignment"
                                    class="dropdown-item"
                                    @click="editor.chain().focus().setTextAlign(alignment).run()"
                                    :class="{ active: editor.isActive({ textAlign: alignment }) }"
                                >
                                    <span
                                        class="icon-lg icon align-text-top"
                                        :class="{
                                            'icon-glyphicons-basic-paragraph-left': alignment === 'left',
                                            'icon-glyphicons-basic-paragraph-center': alignment === 'center',
                                            'icon-glyphicons-basic-paragraph-right': alignment === 'right',
                                        }"
                                    >
                                    </span>
                                </a>
                            </div>
                        </div>
                        <button
                            class="btn btn-sm btn-light"
                            @click="editor.chain().focus().toggleBulletList().run()"
                            :class="{ active: editor.isActive('bulletList') }"
                        >
                            <span class="icon-lg icon icon-glyphicons-basic-list align-text-top"></span>
                        </button>
                        <div class="btn-group" role="group">
                            <button
                                type="button"
                                class="btn btn-sm btn-light toolbar-toggle"
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                            >
                                <span
                                    class="icon-lg icon align-text-top"
                                    :class="{
                                        'icon-glyphicons-basic-list-numbers': !editor.isActive('orderedList', {
                                            type: 'a',
                                        }),
                                        'icon-glyphicons-basic-list-letters': editor.isActive('orderedList', {
                                            type: 'a',
                                        }),
                                    }"
                                >
                                </span>
                                <span class="align-middle icon icon-glyphicons-basic-chevron-down icon-chevron"></span>
                            </button>
                            <div class="dropdown-menu shadow-sm toolbar-dropdown">
                                <a
                                    v-for="listType in ['1', 'a']"
                                    :key="listType"
                                    class="dropdown-item"
                                    @click="editor.chain().focus().toggleOrderedList(listType).run()"
                                    :class="{ active: editor.isActive('orderedList', { type: listType }) }"
                                >
                                    <span
                                        class="icon-lg icon align-text-top"
                                        :class="{
                                            'icon-glyphicons-basic-list-numbers': listType === '1',
                                            'icon-glyphicons-basic-list-letters': listType === 'a',
                                        }"
                                    >
                                    </span>
                                </a>
                            </div>
                        </div>
                        <div class="btn-group" role="group">
                            <button
                                type="button"
                                class="btn btn-sm btn-light toolbar-toggle"
                                :class="{ active: editor.isActive('textStyle', { color: /^#|^rgb.*?/ }) }"
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                            >
                                <span class="icon-lg icon icon-glyphicons-basic-palette align-text-top"></span>
                                <span class="align-middle icon icon-glyphicons-basic-chevron-down icon-chevron"></span>
                            </button>
                            <div class="dropdown-menu theme-color-dropdown shadow-sm">
                                <h6 class="dropdown-header px-2 pb-1 tiny">Palette</h6>
                                <a
                                    v-for="(hexCode, index) in themeHexColors"
                                    :key="hexCode + index"
                                    class="dropdown-item theme-color text-center my-2 rounded"
                                    :class="{
                                        'color-active': editor.isActive('textStyle', { color: hexCode }),
                                    }"
                                    :style="{ 'background-color': hexCode }"
                                    @click="setColor({ target: { value: hexCode } })"
                                ></a>
                                <router-link
                                    :to="caseBaseUrl + '/settings?view=styles'"
                                    class="dropdown-item tiny text-primary theme-color-link pb-0 pt-1"
                                    >Manage</router-link
                                >
                                <div class="dropdown-divider"></div>
                                <h6 class="dropdown-header px-2 tiny pb-0">Custom</h6>
                                <input
                                    type="color"
                                    @input="setColor($event)"
                                    :value="editorColorFormatted"
                                    class="dropdown-item form-control btn btn-gray theme-color mb-2 mt-1 theme-color-custom border-0 rounded"
                                />
                                <div class="dropdown-divider"></div>
                                <a
                                    class="dropdown-item px-2 color-reset"
                                    @click="editor.chain().focus().unsetColor().run()"
                                >
                                    <span class="icon icon-sm icon-glyphicons-basic-drop-off mr-1"></span>
                                    <span>Reset</span>
                                </a>
                            </div>
                        </div>
                        <button
                            class="btn btn-sm btn-light"
                            :class="{ active: showTableTools }"
                            @click.stop="toggleTableTools()"
                        >
                            <span class="icon-lg icon icon-glyphicons-basic-table align-text-top"></span>
                            <span class="align-middle icon icon-glyphicons-basic-chevron-down icon-chevron"></span>
                        </button>
                        <button class="btn btn-light btn-sm" type="button" @click.stop="toggleUrlTools()">
                            <span class="icon-lg icon-glyphicons-basic-link align-middle"></span>
                            <span class="align-middle icon icon-glyphicons-basic-chevron-down icon-chevron"></span>
                        </button>
                        <div class="btn-group" role="group">
                            <button
                                type="button"
                                class="btn btn-sm btn-light toolbar-toggle"
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                            >
                                <span class="d-inline-block">🙂</span>
                                <span class="align-middle icon icon-glyphicons-basic-chevron-down icon-chevron"></span>
                            </button>
                            <div class="dropdown-menu shadow-sm emoji-dropdown p-2">
                                <button
                                    v-for="(emoji, index) in editorEmojiList"
                                    :key="index"
                                    @click="editor.commands.insertEmoji(emoji)"
                                    class="btn btn-transparent"
                                >
                                    <div>{{ emoji }}</div>
                                </button>
                            </div>
                        </div>
                        <button class="btn btn-sm btn-light" @click="editor.chain().focus().setHorizontalRule().run()">
                            ―
                        </button>
                    </div>
                    <div v-if="allowFormElements" class="flex-grow-0 flex-shrink-0 ml-2">
                        <button class="btn btn-sm btn-primary nowrap" @click="editor.chain().focus().setInput().run()">
                            <span class="icon icon-100 icon-glyphicons-basic-plus mr-1"></span>
                            <span>Insert Input</span>
                        </button>
                    </div>
                    <div class="flex-grow-1 d-flex justify-content-end">
                        <slot name="extra-toolbar-items"></slot>
                    </div>
                </div>
                <div v-show="showTableTools" class="mt-1">
                    <div class="btn-group table-btns">
                        <button
                            class="btn btn-sm btn-light"
                            @click="editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()"
                        >
                            <span>Insert Table</span>
                        </button>
                        <div class="btn-group" role="group">
                            <button
                                type="button"
                                class="btn btn-sm btn-light"
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                            >
                                <span>Column</span>
                                <span
                                    class="align-middle icon icon-glyphicons-basic-chevron-down icon-chevron ml-1"
                                ></span>
                            </button>
                            <div class="dropdown-menu shadow-sm table-dropdown">
                                <a class="dropdown-item" @click="editor.chain().focus().addColumnBefore().run()">
                                    <span class="icon-xs icon-glyphicons-basic-plus icon-mr"></span>
                                    <span>Left</span>
                                </a>
                                <a class="dropdown-item" @click="editor.chain().focus().addColumnAfter().run()">
                                    <span class="icon-xs icon-glyphicons-basic-plus icon-mr"></span>
                                    <span>Right</span>
                                </a>
                                <a class="dropdown-item" @click="editor.chain().focus().deleteColumn().run()">
                                    <span class="icon-xs icon-glyphicons-basic-minus icon-mr"></span>
                                    <span>Remove</span>
                                </a>
                            </div>
                        </div>
                        <div class="btn-group" role="group">
                            <button
                                type="button"
                                class="btn btn-sm btn-light"
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                            >
                                <span>Row</span>
                                <span
                                    class="align-middle icon icon-glyphicons-basic-chevron-down icon-chevron ml-1"
                                ></span>
                            </button>
                            <div class="dropdown-menu shadow-sm table-dropdown">
                                <a class="dropdown-item" @click="editor.chain().focus().addRowBefore().run()">
                                    <span class="icon-xs icon-glyphicons-basic-plus icon-mr"></span>
                                    <span>Above</span>
                                </a>
                                <a class="dropdown-item" @click="editor.chain().focus().addRowAfter().run()">
                                    <span class="icon-xs icon-glyphicons-basic-plus icon-mr"></span>
                                    <span>Below</span>
                                </a>
                                <a class="dropdown-item" @click="editor.chain().focus().deleteRow().run()">
                                    <span class="icon-xs icon-glyphicons-basic-minus icon-mr"></span>
                                    <span>Remove</span>
                                </a>
                            </div>
                        </div>
                        <div class="btn-group" role="group">
                            <button
                                type="button"
                                class="btn btn-sm btn-light"
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                            >
                                <span>Cell</span>
                                <span
                                    class="align-middle icon icon-glyphicons-basic-chevron-down icon-chevron ml-1"
                                ></span>
                            </button>
                            <div class="dropdown-menu shadow-sm table-dropdown">
                                <a class="dropdown-item" @click="editor.chain().focus().splitCell().run()">
                                    <span>Split</span>
                                </a>
                                <a class="dropdown-item" @click="editor.chain().focus().mergeCells().run()">
                                    <span>Merge</span>
                                </a>
                                <a
                                    class="dropdown-item"
                                    @click="editor.chain().focus().toggleWordBreak('keep-all').run()"
                                >
                                    <span>Expand</span>
                                </a>
                            </div>
                        </div>
                        <div class="btn-group" role="group">
                            <button
                                type="button"
                                class="btn btn-sm btn-light"
                                data-toggle="dropdown"
                                aria-haspopup="true"
                                aria-expanded="false"
                            >
                                <span>Header</span>
                                <span
                                    class="align-middle icon icon-glyphicons-basic-chevron-down icon-chevron ml-1"
                                ></span>
                            </button>
                            <div class="dropdown-menu shadow-sm table-dropdown">
                                <a class="dropdown-item" @click="editor.chain().focus().toggleHeaderRow().run()">
                                    <span>Toggle Row</span>
                                </a>
                                <a class="dropdown-item" @click="editor.chain().focus().toggleHeaderCell().run()">
                                    <span>Toggle Cell</span>
                                </a>
                            </div>
                        </div>
                        <button class="btn btn-sm btn-light" @click="editor.chain().focus().deleteTable().run()">
                            <span class="icon icon-glyphicons-basic-bin"></span>
                        </button>
                    </div>
                </div>
                <div v-show="showUrlTools" class="my-1" style="max-width: 400px">
                    <div class="input-group">
                        <input
                            v-model="editorURL"
                            ref="urlInput"
                            type="text"
                            class="form-control h-100"
                            placeholder="Enter a url..."
                        />
                        <div class="input-group-append">
                            <button
                                class="btn btn-light btn-sm"
                                type="button"
                                @click="setLinkUrl()"
                                :disabled="!highlightedText"
                            >
                                <span class="icon-100 icon-glyphicons-basic-link align-middle mr-1"></span>
                                <span class="align-middle">Set Link</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            <editor-content class="rich-text-content" :editor="editor" />
            <v-style> .rich-text-editor{ min-height: {{ minHeight }} !important; } </v-style>
        </div>
    </div>
</template>

<script>
import { Node, Extension, mergeAttributes } from "@tiptap/core"
import { Plugin, PluginKey } from "prosemirror-state"
import Document from "@tiptap/extension-document"
import Paragraph from "@tiptap/extension-paragraph"
import Text from "@tiptap/extension-text"
import { Editor, EditorContent } from "@tiptap/vue-2"
import Bold from "@tiptap/extension-bold"
import Italic from "@tiptap/extension-italic"
import Underline from "@tiptap/extension-underline"
import Heading from "@tiptap/extension-heading"
import TextAlign from "@tiptap/extension-text-align"
import BulletList from "@tiptap/extension-bullet-list"
import OrderedList from "@tiptap/extension-ordered-list"
import ListItem from "@tiptap/extension-list-item"
import Link from "@tiptap/extension-link"
import TextStyle from "@tiptap/extension-text-style"
import Subscript from "@tiptap/extension-subscript"
import Superscript from "@tiptap/extension-superscript"
import History from "@tiptap/extension-history"
import Table from "@tiptap/extension-table"
import TableRow from "@tiptap/extension-table-row"
import TableCell from "@tiptap/extension-table-cell"
import TableHeader from "@tiptap/extension-table-header"
import Gapcursor from "@tiptap/extension-gapcursor"
import HorizontalRule from "@tiptap/extension-horizontal-rule"
import Placeholder from '@tiptap/extension-placeholder'

const defaultTextColor = "#3a3f44"

// Find all nodes of type "input" in a given tiptap contentArray.
const findInputNodes = function (contentArray, foundNodes = []) {
    contentArray.forEach((node) => {
        if (node.type.name == "input") {
            foundNodes.push(node)
        }
        if (node.content && node.content.content && node.content.content.length > 0) {
            findInputNodes(node.content.content, foundNodes)
        }
    })
    return foundNodes
}

// Generate and return a locally unique ID.
const createUniqueId = function () {
    return String(Math.floor(Math.random() * 100000000000000))
}

// Returns a custom tiptap extension which represents an HTML input element.
// Accepts a vue instance (i.e. *this* within the vue component) so that I can call
// vue methods. Organized things this way to avoid piling this extension code into
// the vue component definition below, for consistency with the way the rest of the
// extensions are defined.
const createInputExtension = function (vueInstance) {
    // A custom extension used to add form input elements to rich text - used for
    // form-building functionality.
    return Node.create({
        name: "input",
        draggable: true,
        group: "inline",
        inline: true,
        // allowGapCursor: true,
        addOptions() {
            return {
                nested: false,
                HTMLAttributes: {},
            }
        },
        addAttributes() {
            return {
                class: {
                    default: "form-control form-control-sm",
                },
                // An attribute used to make it easier to find input elements within rich text html.
                dataBlockType: {
                    default: "input",
                    parseHTML: (element) => element.getAttribute("data-block-type"),
                    renderHTML: (attributes) => {
                        return {
                            "data-block-type": attributes.dataBlockType,
                        }
                    },
                },
                // Disable autocomplete for all inputs.
                autocomplete: {
                    default: false,
                    parseHTML: (element) => element.getAttribute("autocomplete"),
                    renderHTML: (attributes) => {
                        return {
                            autocomplete: attributes.autocomplete,
                        }
                    },
                },
                // A locally unique ID assigned to the input element.
                blockId: {
                    default: null,
                    parseHTML: (element) => element.getAttribute("data-block-id"),
                    renderHTML: (attributes) => {
                        return {
                            "data-block-id": attributes.blockId,
                        }
                    },
                },
            }
        },
        // Hook into ProseMirror props.
        addProseMirrorPlugins() {
            return [
                new Plugin({
                    key: new PluginKey("eventHandler"),
                    props: {
                        handleClick(view, pos, event) {
                            let blockId = event.target.getAttribute("data-block-id")
                            if (!blockId) {
                                // Don't emit any event if there's no block ID on the clicked element.
                                return
                            }
                            vueInstance.$emit("block-click", blockId)
                        },
                        handlePaste(view, event, slice) {
                            let inputNodes = findInputNodes(slice.content.content)
                            inputNodes.forEach((node) => {
                                // This handles cut & paste (vs. *copy* & paste). With cut and paste, the input should
                                // which should retain the same block ID. With copy & paste, a new block ID should be
                                // generated and assigned.
                                // Query the rich text editor for another input with the same block ID.
                                let copiedBlockId = node.attrs.blockId
                                let matchingBlock = vueInstance.editorContainer.querySelectorAll(
                                    `[data-block-id='${copiedBlockId}']`
                                )
                                // Only set a new unique ID if there's another input with the same ID found.
                                if (matchingBlock.length) {
                                    // A matching block was found, so this means the block was copy-pasted.
                                    // Give the block a brand new ID and emit an event for the creation of
                                    // a new block.
                                    let blockId = createUniqueId()
                                    node.attrs.blockId = blockId
                                    vueInstance.$emit("block-created", {
                                        blockId: blockId,
                                        copiedBlockId: copiedBlockId,
                                    })
                                }
                            })
                        },
                    },
                }),
            ]
        },
        addCommands() {
            return {
                setInput:
                    () =>
                    ({ chain }) => {
                        // Check if an input is currently selected in the editor using vanilla javascript.
                        let inputIsSelected = Boolean(
                            vueInstance.editorContainer.querySelector(
                                "[data-block-type='input'].ProseMirror-selectednode"
                            )
                        )
                        if (inputIsSelected) {
                            // Prevent the editor from overwriting an existing input block
                            // with a fresh one - this probably isn't what the user intended to do.
                            return
                        }
                        let blockId = createUniqueId()
                        vueInstance.$emit("block-created", { blockId: blockId, copiedBlockId: null })
                        return chain()
                            .insertContent({
                                type: this.name,
                                attrs: {
                                    blockId: blockId,
                                },
                            })
                            .run()
                    },
            }
        },
        parseHTML() {
            return [{ tag: "input" }]
        },
        renderHTML({ HTMLAttributes }) {
            return ["input", mergeAttributes(this.options.HTMLAttributes, HTMLAttributes)]
        },
    })
}

const EmojiExtension = Extension.create({
    name: "emoji",
    addCommands() {
        return {
            insertEmoji:
                (emoji) =>
                ({ tr, state }) => {
                    const { selection } = state
                    const position = selection.$cursor ? selection.$cursor.pos : selection.$to.pos
                    tr.insertText(emoji, position)
                    return true
                },
        }
    },
})

// A custom extension for setting font color. Doesn't respect the font color of HTML
// that's pasted in, unless it was set by this editor (does this via data-color attribute).
const CustomColor = TextStyle.extend({
    name: "color",
    addOptions: {
        types: ["textStyle"],
    },
    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    color: {
                        default: null,
                        renderHTML: (attributes) => {
                            if (!attributes.color) {
                                return {}
                            }
                            // Embed the color in a special attribute that we can use
                            // to tell that the color was actually set by our editor.
                            return {
                                style: `color: ${attributes.color}`,
                                "data-color": attributes.color,
                            }
                        },
                        parseHTML: (element) => element.getAttribute("data-color"),
                    },
                },
            },
        ]
    },
    addCommands() {
        return {
            setColor:
                (color) =>
                ({ chain }) => {
                    return chain().setMark("textStyle", { color }).run()
                },
            unsetColor:
                () =>
                ({ chain }) => {
                    return chain().setMark("textStyle", { color: null }).removeEmptyTextStyle().run()
                },
        }
    },
})

// A custom extension for setting font size.
const FontSize = TextStyle.extend({
    name: "fontSize",
    addOptions: {
        types: ["textStyle"],
    },
    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    fontSize: {
                        default: null,
                        renderHTML: (attributes) => {
                            if (!attributes.fontSize) {
                                return {}
                            }
                            // Embed the font size in a special attribute we can use to
                            // tell that the color was actually set by our editor.
                            return {
                                style: `font-size: ${attributes.fontSize}`,
                                "data-font-size": attributes.fontSize,
                            }
                        },
                        parseHTML: (element) => element.getAttribute("data-font-size"),
                    },
                },
            },
        ]
    },
    addCommands() {
        return {
            setFontSize:
                (fontSize) =>
                ({ chain }) => {
                    return chain().setMark("textStyle", { fontSize }).run()
                },
            unsetFontSize:
                () =>
                ({ chain }) => {
                    return chain().setMark("textStyle", { fontSize: null }).run()
                },
        }
    },
})

const CustomParagraph = Paragraph.extend({
    addOptions() {
        return {
            ...this.parent?.(),
            enableAutoDir: false,
        }
    },
    addAttributes() {
        return {
            class: {
                default: "custom-p-normal",
                renderHTML: (attributes) => {
                    let config = {
                        class: `${attributes.class}`,
                        "data-class": attributes.class,
                    }
                    if (this.options.enableAutoDir) {
                        config["dir"] = "auto"
                    }
                    return config
                },
                parseHTML: (element) => {
                    return element.getAttribute("data-class")
                },
            },
        }
    },
    addCommands() {
        return {
            setParagraph:
                (args) =>
                ({ commands }) => {
                    if (!args) {
                        return commands.setNode("paragraph")
                    }
                    return commands.setNode("paragraph", { class: args.class })
                },
        }
    },
})

const AutoDirHeading = Heading.extend({
    renderHTML({ node, HTMLAttributes }) {
        // Set the dir="auto" attribute for all heading nodes.
        HTMLAttributes.dir = "auto"
        // Return the heading element with the updated attributes.
        return [
            `h${node.attrs.level}`,
            HTMLAttributes,
            0,
        ]
    },
})

const WordBreak = TextStyle.extend({
    name: "wordBreak",
    addOptions: {
        types: ["textStyle"],
    },
    addGlobalAttributes() {
        return [
            {
                types: this.options.types,
                attributes: {
                    wordBreak: {
                        default: null,
                        renderHTML: (attributes) => {
                            if (!attributes.wordBreak) {
                                return {}
                            }
                            return {
                                style: `word-break: ${attributes.wordBreak}`,
                            }
                        },
                        parseHTML: (element) => {
                            return element.style["word-break"]
                        },
                    },
                },
            },
        ]
    },
    addCommands() {
        return {
            toggleWordBreak:
                (wordBreak) =>
                ({ chain }) => {
                    return chain().toggleMark("textStyle", { wordBreak }).run()
                },
        }
    },
})

const CustomOrderedList = OrderedList.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            type: {
                default: "1",
                renderHTML: (attributes) => {
                    return {
                        type: `${attributes.type}`,
                    }
                },
                parseHTML: (element) => {
                    return element.getAttribute("type")
                },
            },
        }
    },
    addCommands() {
        return {
            toggleOrderedList:
                (listType) =>
                ({ chain }) => {
                    // For some reason this command chain doesn't 'toggle' the ordered list,
                    // it just enables the list (or switches between list types).
                    return chain()
                        .toggleList(this.name, this.options.itemTypeName)
                        .updateAttributes("orderedList", { type: listType })
                },
        }
    },
})

export default {
    name: "RichText",
    props: {
        value: {
            type: String,
            default: "",
        },
        // When true, buttons to add form elements to rich text will be shown (currently, only
        // basic input types are supported).
        allowFormElements: {
            type: Boolean,
            default: false,
        },
        // The minimum height of the text editor.
        minHeight: {
            type: String,
            default() {
                return "100px"
            },
        },
        placeholder: {
            type: String,
            default() {
                return "Type something here..."
            },
            required: false,
        },
        defaultContent: {
            type: String,
            default: null,
        },
    },
    components: {
        EditorContent,
    },
    data() {
        return {
            editor: null,
            editorURL: null,
            textRed: null,
            textGreen: null,
            textBlue: null,
            fontSizes: ["10px", "12px", "14px", "16px", "18px", "20px"],
            orderedListTypes: ["1", "a"],
            currentColor: "#222222",
            showTableTools: false,
            showUrlTools: false,
            editorContainer: null,
            editorEmojiList: [
                "🙂",
                "😕",
                "👍",
                "👎",
                "✋",
                "👉",
                "👇",
                "💡",
                "⚡",
                "❓",
                "❗️",
                "✅",
                "❌",
                "⛔️",
                "⚠️",
                "🛑",
                "🏁",
                "✏️",
                "✍️",
                "⌛️",
                "🚦",
                "🔍",
                "🎯",
                "💬",
                "💭",
            ],
        }
    },
    computed: {
        // [Temporary] This is used to enable adding the dir='auto' attribute to paragraphs and
        // headings, to support right-to-left for one specific user group. Once tested, this conditional
        // will be removed and the dir attribute will be added everywhere.
        enableAutoDir() {
            return this.$store.getters.currentOrganizationId === 584
        },
        // Returns true if the text align node is inactive (not applied).
        textAlignIsInactive() {
            return (
                !this.editor.isActive({ textAlign: "left" }) &&
                !this.editor.isActive({ textAlign: "right" }) &&
                !this.editor.isActive({ textAlign: "center" })
            )
        },
        // Return the editor's color (of the selection), formatted as a hex string.
        editorColorFormatted() {
            let textStyle = this.editor.getAttributes("textStyle").color
            if (!textStyle) {
                return defaultTextColor
            }
            if (textStyle.startsWith("rgb")) {
                // Try to convert the RGB string to hex, but silence any errors that occur.
                try {
                    textStyle = rgbToHex(textStyle)
                } catch (error) {
                    console.log(error)
                    return defaultTextColor
                }
            }
            return textStyle
        },
        themeHexColors() {
            return this.$store.getters.caseThemeHexColors
        },
        caseBaseUrl() {
            return this.$store.getters.caseBaseUrl
        },
        mixedTextActive() {
            return (
                !this.editor.isActive("heading") &&
                !this.editor.isActive("paragraph", { class: "custom-p-normal" }) &&
                !this.editor.isActive("paragraph", { class: "custom-p-sub" })
            )
        },
        extensions() {
            let HeadingExt = this.enableAutoDir ? AutoDirHeading : Heading
            let extensions = [
                Document,
                CustomParagraph.configure({
                    enableAutoDir: this.enableAutoDir,
                }),
                Text,
                Bold,
                Italic,
                Underline,
                HeadingExt,
                TextAlign.configure({
                    types: ["heading", "paragraph"],
                }),
                BulletList,
                ListItem,
                CustomOrderedList,
                TextStyle,
                CustomColor,
                Link,
                FontSize,
                Subscript,
                Superscript,
                History,
                Table.configure({
                    resizable: false,
                    HTMLAttributes: {
                        class: "table table-bordered my-2",
                    },
                }),
                TableRow,
                TableHeader,
                TableCell,
                WordBreak,
                Gapcursor,
                HorizontalRule,
                EmojiExtension,
                Placeholder.configure({
                    placeholder: this.placeholder,
                }),
            ]
            if (this.allowFormElements) {
                extensions.push(createInputExtension(this))
            }
            return extensions
        },
        highlightedText() {
            if (!this.editor) return ""
            const { from, to } = this.editor.state.selection
            return this.editor.state.doc.textBetween(from, to, " ")
        },
    },
    methods: {
        // Set the highlighted text as a link with the entered URL.
        setLinkUrl() {
            this.editor
                .chain()
                .focus()
                .extendMarkRange("link")
                .setLink({
                    href: this.editorURL,
                    target: "_blank",
                })
                .run()
            this.editorURL = null
        },
        setColor($event) {
            // Make sure to leave the 'focus()' method out here - it kills Safari, causing
            // the page to reload.
            this.editor.chain().setColor($event.target.value).run()
        },
        toggleTableTools() {
            this.showTableTools = !this.showTableTools
            if (this.showTableTools) {
                // Don't show table and URL toolbars at the same time.
                this.showUrlTools = false
            }
        },
        toggleUrlTools() {
            this.showUrlTools = !this.showUrlTools
            if (this.showUrlTools) {
                // Don't show table and URL toolbars at the same time.
                this.showTableTools = false
                let vm = this
                this.$nextTick(() => {
                    vm.$refs.urlInput.focus()
                })
            }
        },
        focus() {
            this.editor.chain().focus()
        },
        handleBlur(event) {
            this.$emit("blur", event)
        },
    },
    watch: {
        // This is a hack - this watcher makes sure that changes to the value of the text editor
        // in the parent are captured upon initial load.
        value(value) {
            const isSame = this.editor.getHTML() === value
            if (isSame) {
                return
            }
            this.editor.commands.setContent(this.value, false)
        },
    },
    mounted() {
        if (!this.value && this.defaultContent) {
            this.value = this.defaultContent
        }
        // Initialize the tiptap text editor.
        this.editor = new Editor({
            content: this.value,
            editorProps: {
                attributes: {
                    class: "form-control rich-text-editor",
                },
            },
            extensions: this.extensions,
            // Reflect the current rich text html in the parent.
            onUpdate: () => {
                let html = this.editor.getHTML()
                let headingEmpty = ["<h1></h1>", "<h2></h2>", "<h3></h3>"]
                if (this.editor.isEmpty || headingEmpty.includes(html)) {
                    this.$emit("input", "")
                } else {
                    this.$emit("input", html)
                }
            },
        })
        // Store the editor container to be used for vanilla js document queries elsewhere
        // within this component.
        this.editorContainer = this.$refs["editor-container"]
        // Add a blur event listener.
        this.editor.on("blur", ({ event, editor }) => {
            this.handleBlur(event, editor)
        })
    },
    beforeUnmount() {
        this.editor.destroy()
    },
}

function componentToHex(num) {
    var hex = num.toString(16)
    return hex.length == 1 ? "0" + hex : hex
}

function rgbToHex(rgbString) {
    var match = rgbString.match(/rgba?\((\d{1,3}), ?(\d{1,3}), ?(\d{1,3})\)?(?:, ?(\d(?:\.\d?))\))?/)
    if (!match) {
        match = rgbString.match(/rgba?\((\d{1,3}) ?(\d{1,3}) ?(\d{1,3})\)?(?: ?(\d(?:\.\d?))\))?/)
    }
    return "#" + componentToHex(Number(match[1])) + componentToHex(Number(match[2])) + componentToHex(Number(match[3]))
}
</script>

<style lang="scss" scoped>
@import "@/assets/styles/variables.scss";

.toolbar-dropdown {
    max-width: 6rem;
    min-width: 4rem !important;
}

.emoji-dropdown {
    min-width: 11rem !important;
}

.table-dropdown {
    max-width: 10rem;
    min-width: 4rem !important;
}

.color-input {
    width: 35px;
    padding-left: 6px;
    padding-right: 6px;
    padding-top: 4px;
    padding-bottom: 4px;
}

.icon-table-tools {
    margin-right: 5px;
}

.text-label {
    font-size: 12px;
    min-width: 84px;
}

.heading-label {
    margin-left: 4px;
    margin-right: 4px;
}

.theme-color-dropdown {
    max-width: 6rem;
    min-width: 4.5rem !important;
}

.theme-color {
    height: 24px;
    width: 30px;
    margin-right: auto;
    margin-left: auto;
    border: solid 2px transparent;
    padding: 0.25rem 1rem;
}

.theme-color-link {
    padding-right: 12px;
    padding-left: 12px;
}

.theme-color-custom {
    width: 50px;
    height: 34px;
    padding-left: 3px;
    padding-right: 3px;
    padding-top: 3px;
    padding-bottom: 3px;
}

.color-active {
    border-color: $primary !important;
}

.color-reset {
    font-size: 14px;
}

.dropdown-font-size {
    font-size: 15px;
}

.icon-chevron {
    font-size: 10px;
    margin-left: 2px;
    color: $gray-800;
}

.btn {
    padding-left: 6.5px;
    padding-right: 6.5px;
}

.toolbar-toggle {
    white-space: nowrap;
}

.toolbar-vert-adjust {
    position: relative;
    top: -1px;
}

.btn-group-end {
    border-bottom-right-radius: $border-radius-sm !important;
    border-top-right-radius: $border-radius-sm !important;
}
</style>

<style lang="scss">
@import "@/assets/styles/variables.scss";

.ProseMirror {
    p {
        word-break: break-word;
    }

    table {
        width: 100%;
        margin: 0;
        margin-bottom: 10px;
        margin-top: 10px;
        overflow: hidden;
        max-width: 100%;

        .selectedCell {
            background: $light;
        }

        th {
            background-color: $offwhite;
            padding: 8px !important;
        }

        td {
            padding: 8px !important;
            word-wrap: break-word;
        }
    }

    input.form-control {
        display: inline-block;
        width: auto;
        &.ProseMirror-selectednode {
            background-color: lighten($primary, 40%);
        }
        background-color: white;
        margin-bottom: 2px;
        margin-top: 2px;
    }

    .is-editor-empty:first-child::before {
        color: $input-placeholder-color;
        content: attr(data-placeholder);
        float: left;
        height: 0;
        pointer-events: none;
        font-size: 1rem;
        font-weight: 400;
    }
}
</style>